import { PDPSwarovskiProductFrontend, PDPSwarovskiProductVariantFrontend } from "./types";
import { nonEmpty, VariantAttribute } from "@swo/shared";
import { attributeEq } from "./attributes";
import { pipe } from "fp-ts/lib/function";
import * as NEA from "fp-ts/lib/NonEmptyArray";
import * as O from "fp-ts/lib/Option";
import { NonEmptyArray } from "fp-ts/lib/NonEmptyArray";

type VariantSelectionForAttributes = {
  variant: PDPSwarovskiProductVariantFrontend;
};

export function selectVariantForAttributes(
  selectedProduct: PDPSwarovskiProductFrontend,
  constraints: SelectionNextStep,
): VariantSelectionForAttributes {
  const selectedVariant = findSelectedVariantForPreciseAttributesSelection(
    selectedProduct,
    constraints.desiredAttributesSelection,
  );

  if (!selectedVariant) {
    return recoverAttributesSelection(selectedProduct, constraints);
  }

  return { variant: selectedVariant };
}

export function findSelectedVariantForPreciseAttributesSelection(
  selectedProduct: PDPSwarovskiProductFrontend,
  desiredAttributesSelection: Array<VariantAttribute>,
): PDPSwarovskiProductVariantFrontend | undefined {
  return selectedProduct.variants.filter(variant => {
    const variantAttributes = variant.attributes.unique || [];

    return desiredAttributesSelection.every(desiredAttr =>
      variantAttributes.some(v => attributeEq.equals(v, desiredAttr)),
    );
  })[0];
}

export function recoverAttributesSelection(
  selectedProduct: PDPSwarovskiProductFrontend,
  constrains: SelectionNextStep,
): VariantSelectionForAttributes {
  return pipe(
    constrains.desiredAttributesSelection,
    NEA.fromArray,
    O.fold(
      () => {
        return { variant: selectedProduct.variants[0] };
      },
      desiredAttributesSelection => {
        const next = removeOneAttribute({
          desiredAttributesSelection,
          stickyAttributesSelectionIndex: constrains.stickyAttributesSelectionIndex,
        });

        return selectVariantForAttributes(selectedProduct, next);
      },
    ),
  );
}

type SelectionConstrains = {
  desiredAttributesSelection: NonEmptyArray<VariantAttribute>;
  stickyAttributesSelectionIndex: number | undefined;
};

export type SelectionNextStep = {
  desiredAttributesSelection: Array<VariantAttribute>;
  stickyAttributesSelectionIndex: number | undefined;
};

export function removeOneAttribute(constraints: SelectionConstrains): SelectionNextStep {
  const { stickyAttributesSelectionIndex, desiredAttributesSelection } = constraints;

  if (typeof stickyAttributesSelectionIndex === "undefined") {
    return {
      desiredAttributesSelection: desiredAttributesSelection.slice(0, -1),
      stickyAttributesSelectionIndex: undefined,
    };
  }

  const stickyAttribute: VariantAttribute | undefined = desiredAttributesSelection[stickyAttributesSelectionIndex];

  if (typeof stickyAttribute === "undefined") {
    // FIXME:
    // Likely, wrong index - invalid state exception
    return {
      desiredAttributesSelection: desiredAttributesSelection.slice(0, -1),
      stickyAttributesSelectionIndex: undefined,
    };
  }

  const lastDesiredAttribute = desiredAttributesSelection[desiredAttributesSelection.length - 1];

  if (lastDesiredAttribute && lastDesiredAttribute.name === stickyAttribute.name) {
    return {
      desiredAttributesSelection: [...desiredAttributesSelection.slice(0, -2), lastDesiredAttribute].filter(nonEmpty),
      stickyAttributesSelectionIndex: undefined,
    };
  } else {
    return {
      desiredAttributesSelection: desiredAttributesSelection.slice(0, -1),
      stickyAttributesSelectionIndex: undefined,
    };
  }
}
